/* Gigglz message core: typography, panel geometry, text helpers and an
 * off-screen measurer for inside-card messages. Shared by the personalise
 * page (personalise-message.js) and the Send List page. No page-specific DOM.
 *
 * Any change to the maps below changes what the render worker must honour;
 * keep them in step with aws/shared/rendering.
 */
(function () {
  'use strict';

  const FONT_MAP = {
    'font-classic': '"Noto Sans", Arial, sans-serif',
    'font-love': '"Dancing Script", cursive',
    'font-friendly': '"Patrick Hand", cursive',
    'font-secret': '"Special Elite", cursive',
    'font-scribble': '"Reenie Beanie", cursive',
    'font-statement': '"Paytone One", Arial, sans-serif',
    'font-christmas': '"Mountains of Christmas", cursive'
  };

  const FONT_LABELS = {
    'font-classic': 'Classic',
    'font-love': 'Love Letter',
    'font-friendly': 'Friendly',
    'font-secret': 'Secret Agent',
    'font-scribble': 'Scribble',
    'font-statement': 'Statement',
    'font-christmas': 'Christmas'
  };

  const DEFAULT_FONT_KEY = 'font-classic';
  const SIZE_MAP = { XS: 32, Small: 50, Medium: 74, Large: 100, XL: 129 };
  const DEFAULT_SIZE_KEY = 'Medium';
  const LINE_HEIGHT_MULTIPLIER = 1.25;
  const PANEL_SPEC_MAP = {
    '7x5': { width: 1176, height: 1629, padX: 96, padY: 92 },
    'a4': { width: 1964, height: 2753, padX: 96, padY: 92 }
  };

  function normaliseFontKey(key) {
    return FONT_MAP[key] ? key : DEFAULT_FONT_KEY;
  }

  function normaliseSizeKey(key) {
    return SIZE_MAP[key] ? key : DEFAULT_SIZE_KEY;
  }

  function normaliseRateProfile(value) {
    const raw = String(value || '').trim().toLowerCase();
    if (raw === 'a4' || raw.includes('a4') || raw.includes('jumbo')) return 'a4';
    return '7x5';
  }

  function getPanelSpec(rateProfile) {
    return PANEL_SPEC_MAP[normaliseRateProfile(rateProfile)] || PANEL_SPEC_MAP['7x5'];
  }

  // Same numbers personalise-message.js applies to its editor.
  function computeTypography(fontKey, sizeKey) {
    const font = normaliseFontKey(fontKey);
    let px = SIZE_MAP[normaliseSizeKey(sizeKey)];
    if (font === 'font-scribble') px = Math.round(px * 1.20);
    const lh = Math.round(px * LINE_HEIGHT_MULTIPLIER);
    return { fontKey: font, fontFamily: FONT_MAP[font], px, lineHeightPx: lh, lineHeight: (lh / px).toFixed(3) };
  }

  function normaliseText(raw) {
    return String(raw || '')
      .replace(/\u00A0/g, ' ')
      .replace(/\u200B/g, '')
      .replace(/\r/g, '');
  }

  function stripOuterBlankLines(text) {
    return String(text || '')
      .replace(/^(?:[ \t]*\n)+/, '')
      .replace(/(?:\n[ \t]*)+$/, '');
  }

  // Merge fields for Send List: {name}, {signed_by}. Unknown tokens are left as typed.
  const MERGE_TOKEN_RE = /\{(name|signed_by)\}/g;
  function applyMergeFields(template, fields) {
    const f = fields || {};
    return String(template || '').replace(MERGE_TOKEN_RE, (m, key) => {
      const v = f[key];
      return v === undefined || v === null ? m : String(v);
    });
  }
  function hasMergeField(template, key) {
    return String(template || '').indexOf('{' + key + '}') !== -1;
  }

  // Off-screen measurer. measure(text, {fontKey, sizeKey, rateProfile}) ->
  // { usedLines, maxLines, tooLong, usedHeight, innerHeight }, in panel pixels.
  function createMeasurer() {
    let mirror = null;
    function ensure() {
      if (mirror) return mirror;
      mirror = document.createElement('div');
      Object.assign(mirror.style, {
        position: 'absolute', top: '-99999px', left: '-99999px', visibility: 'hidden',
        pointerEvents: 'none', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
        wordBreak: 'break-word', boxSizing: 'border-box', padding: '0', border: '0',
        fontWeight: '400', fontStyle: 'normal', letterSpacing: 'normal', textAlign: 'left'
      });
      document.body.appendChild(mirror);
      return mirror;
    }
    function measure(text, opts) {
      const o = opts || {};
      const spec = getPanelSpec(o.rateProfile);
      const typo = computeTypography(o.fontKey, o.sizeKey);
      const innerWidth = spec.width - (spec.padX * 2);
      const innerHeight = spec.height - (spec.padY * 2);
      const t = stripOuterBlankLines(normaliseText(text));
      const el = ensure();
      el.style.width = innerWidth + 'px';
      el.style.fontFamily = typo.fontFamily;
      el.style.fontSize = typo.px + 'px';
      el.style.lineHeight = typo.lineHeightPx + 'px';
      el.textContent = t;
      const maxLines = Math.max(1, Math.floor((innerHeight + 0.5) / typo.lineHeightPx));
      const usedHeight = t ? el.getBoundingClientRect().height : 0;
      const usedLines = usedHeight === 0 ? 0 : Math.max(1, Math.round(usedHeight / typo.lineHeightPx));
      return { usedLines, maxLines, tooLong: usedHeight > (innerHeight + 1), usedHeight, innerHeight };
    }
    function destroy() { if (mirror && mirror.parentNode) mirror.parentNode.removeChild(mirror); mirror = null; }
    return { measure, destroy };
  }

  window.GigglzMessageCore = Object.freeze({
    FONT_MAP, FONT_LABELS, DEFAULT_FONT_KEY, SIZE_MAP, DEFAULT_SIZE_KEY, LINE_HEIGHT_MULTIPLIER, PANEL_SPEC_MAP,
    normaliseFontKey, normaliseSizeKey, normaliseRateProfile, getPanelSpec, computeTypography,
    normaliseText, stripOuterBlankLines, applyMergeFields, hasMergeField, createMeasurer
  });
})();
